import { useNextStep } from 'nextstepjs';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import { faBan } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Card, Checkbox, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import DrawerBody from './DrawerBody';
import DrawerFooter from './DrawerFooter';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { ObjectTypes, useAnalytics } from 'src/AnalyticsContext';
import ObservationOptionCard, {
    EMPTY_DETAILS,
    NOTE_CHARACTER_LIMIT,
    ObservationDetails,
} from 'src/components/observations/ObservationOptionCard';
import { CareType } from 'src/constants/CareTypes';
import { Drawers } from 'src/constants/Drawers';
import {
    getObservationDisplayName,
    getObservationOptionArrayFromIds,
} from 'src/constants/ObservationTypes';
import { AlertTrainingTours } from 'src/constants/Training';
import useAlertTraining from 'src/hooks/useAlertTraining';
import useCareTypeFeatures from 'src/hooks/useCareTypeFeatures';
import { Task } from 'src/store/sageAdminApi';
import { Resident } from 'src/store/sageCareServiceApi';

export interface AddObservationsDrawerProps extends DrawerComponentProps {
    analyticsPrefix?: string;
    resident?: Resident;
    task?: Task;
    requireSelection?: boolean;
}

const DrawerName = 'Select Observation Type Drawer';

function buildObservationPayloads(
    observations: string[],
    observationDetails: Record<string, ObservationDetails>
) {
    const values: Record<string, string> = {};
    const notes: Record<string, string> = {};
    for (const id of observations) {
        const details = observationDetails[id];
        if (!details) continue;
        if (details.selectedDescriptors.length) {
            values[id] = details.selectedDescriptors.join(',');
        }
        const note = details.note.trim();
        if (note) notes[id] = note;
    }
    return { values, notes };
}

function AddObservationsDrawer(props: AddObservationsDrawerProps) {
    const {
        onNext,
        hasNextDrawer,
        onPrevious,
        title = 'Any new changes to report to Nurse?',
        resident,
        task,
    } = props;

    const { trackEvent, trackDrawerView } = useAnalytics();
    const { spacing, palette } = useTheme();

    const careType = (task?.unit?.careType || resident?.unit?.careType) as CareType;

    const { getFeatures } = useCareTypeFeatures();
    const { isDetailsEnabled: shouldShowDescriptors, options: observationsForCareType } =
        getFeatures(careType).observations;

    const observationTypes = getObservationOptionArrayFromIds(observationsForCareType);
    const { isTrainingAlert, isGuidedTour } = useAlertTraining({ taskRid: task?.id });
    const { startNextStep } = useNextStep();

    const [selectedObservations, setSelectedObservations] = useState<
        Record<string, ObservationDetails>
    >({});
    const [isNoNewChangesSelected, setIsNoNewChangesSelected] = useState(false);

    const selectedObservationsSet = useMemo(
        () => new Set(Object.keys(selectedObservations)),
        [selectedObservations]
    );

    const handleSelectObservation = (observationId: string, isChecked: boolean) => {
        trackEvent('Observation Type Clicked', {
            observationId,
            isChecked,
            objectType: ObjectTypes.Button,
            context: DrawerName,
        });

        if (isChecked) {
            setSelectedObservations((prev) => ({
                ...prev,
                [observationId]: prev[observationId] ?? EMPTY_DETAILS,
            }));
            setIsNoNewChangesSelected(false);
        } else {
            setSelectedObservations((prev) => {
                if (!(observationId in prev)) return prev;
                const next = { ...prev };
                delete next[observationId];
                return next;
            });
        }
    };

    const handleDetailsChange = (observationId: string, details: ObservationDetails) => {
        setSelectedObservations((prev) => ({ ...prev, [observationId]: details }));
    };

    const handleNoNewChangesToggle = () => {
        trackEvent('No New Changes Clicked', {
            isSelected: !isNoNewChangesSelected,
            objectType: ObjectTypes.Button,
            context: DrawerName,
        });

        if (!isNoNewChangesSelected) {
            setSelectedObservations({});
        }
        setIsNoNewChangesSelected((prev) => !prev);
    };

    const hasSelection = isNoNewChangesSelected || selectedObservationsSet.size > 0;

    const isAnyNoteOverLimit = Object.values(selectedObservations).some(
        (details) => details.note.length > NOTE_CHARACTER_LIMIT
    );

    const handleSelectButton = (e: SyntheticEvent) => {
        e.preventDefault();

        const observations = isNoNewChangesSelected ? [] : Array.from(selectedObservationsSet);

        const { values: observationValues, notes: observationNotes } = buildObservationPayloads(
            observations,
            selectedObservations
        );

        trackEvent('Select Button Clicked', {
            objectType: ObjectTypes.Button,
            context: DrawerName,
            selectedObservations: observations,
            selectedDescriptors: observationValues,
            noNewChangesSelected: isNoNewChangesSelected,
        });

        onNext(
            {
                observations,
                observationValues,
                observationNotes,
                ...(observations.length > 0 && {
                    title: 'Anything else to add?',
                    analyticsPrefix: 'Anything else to add?',
                    submitButtonLabel: 'Report to Nurse',
                    successMessage: 'Report sent to Nurse',
                }),
            },
            hasNextDrawer ? undefined : { type: Drawers.ResolveTask }
        );
    };

    useEffect(() => {
        if (isTrainingAlert && isGuidedTour) {
            const timeout = setTimeout(() => {
                startNextStep(AlertTrainingTours.AlertTrainingObservations);
            }, 500);

            return () => clearTimeout(timeout);
        }
    }, [isTrainingAlert, isGuidedTour, startNextStep]);

    useEffect(() => {
        trackDrawerView(DrawerName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <DrawerTitle sx={{ pb: 2, px: 0, pt: 0, gap: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        pb: 1.5,
                        pt: 2.5,
                        pl: 2.5,
                    }}
                >
                    <IconButton
                        aria-label="back"
                        // Drop pass-through outcomes so a changed selection isn't overridden on the way forward
                        onClick={() =>
                            onPrevious(true, [
                                'requireSelection',
                                'careOutcomes',
                                'alertOutcomeType',
                            ])
                        }
                        sx={{
                            color: palette.gray?.[900],
                        }}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </IconButton>
                </Box>
                <Box sx={{ px: 2.5 }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 'bold',
                            textAlign: 'left',
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            </DrawerTitle>
            <DrawerBody sx={{ pb: 2.5, px: 2.5, pt: 0.25 }}>
                <Stack sx={{ gap: 1.5 }} role="group" aria-label="Select Observations">
                    {/* No New Changes button */}
                    <Card
                        onClick={handleNoNewChangesToggle}
                        data-testid="observation-select-no-new-changes"
                        aria-pressed={isNoNewChangesSelected}
                        sx={{
                            borderRadius: 3,
                            boxShadow: 0,
                            px: 2,
                            outline: isNoNewChangesSelected
                                ? `2px solid ${palette.primary.main}`
                                : `1px solid ${palette.grey[300]}`,
                        }}
                    >
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                height: spacing(9.5),
                            }}
                        >
                            <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                                <FontAwesomeIcon
                                    icon={faBan}
                                    color={palette.grey[900]}
                                    size="2x"
                                    fixedWidth
                                />
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    No New Changes
                                </Typography>
                            </Stack>
                            <Checkbox checked={isNoNewChangesSelected} />
                        </Stack>
                    </Card>
                    <Divider
                        sx={{
                            color: palette.grey[600],
                            fontWeight: 600,
                            my: 1,
                        }}
                    >
                        <Typography variant="subtitle1">or</Typography>
                    </Divider>
                    {/* Observation type buttons */}
                    {observationTypes.map((observationType) =>
                        observationType.icon ? (
                            <ObservationOptionCard
                                key={`observation-type-${observationType.id}`}
                                id={observationType.id}
                                label={getObservationDisplayName(observationType.id, careType)}
                                icon={observationType.icon}
                                isSelected={Boolean(selectedObservations[observationType.id])}
                                onSelect={handleSelectObservation}
                                descriptors={
                                    shouldShowDescriptors ? observationType.descriptors : undefined
                                }
                                details={selectedObservations[observationType.id] ?? EMPTY_DETAILS}
                                onDetailsChange={handleDetailsChange}
                                analyticsPrefix={DrawerName}
                            />
                        ) : null
                    )}
                </Stack>
            </DrawerBody>
            <DrawerFooter showShadow sx={{ bgcolor: 'common.white' }}>
                <Stack
                    direction="row"
                    sx={{
                        gap: 1.5,
                        alignItems: 'center',
                    }}
                >
                    <Button
                        fullWidth
                        color="primary"
                        type="submit"
                        variant="contained"
                        onClick={handleSelectButton}
                        disableFocusRipple
                        disableElevation
                        disabled={!hasSelection || isAnyNoteOverLimit}
                        sx={{ height: spacing(7) }}
                    >
                        {selectedObservationsSet.size > 0 ? 'Send to nurse' : 'Select'}
                    </Button>
                </Stack>
            </DrawerFooter>
        </>
    );
}

export default AddObservationsDrawer;
